import { useQuery } from '@tanstack/react-query'
import {
	type ReactNode,
	type RefObject,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react'
import {
	generatePath,
	Outlet,
	useLocation,
	useOutletContext,
	useParams
} from 'react-router'
import { HorizontalScroller } from '@/components/commons/horizontal-scroller'
import { MarqueeBreadcrumbs } from '@/components/commons/marquee-breadcrumbs'
import { TabLink } from '@/components/commons/tab-link'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate } from '@/dates'
import { EventStatusBadge } from '@/features/marquee/components/event-status-badge'
import { ResolutionActions } from '@/features/marquee/components/resolution-actions'
import {
	ctasQueryOptions,
	eventQueryOptions,
	operatorQueryOptions
} from '@/features/marquee/query-options'
import {
	eventPlaceName,
	formatStartTime,
	isMarketManagerAllowed
} from '@/features/marquee/utils'
import { routes } from '@/routes'
import type { MarqueeEventDetail } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/** Single-line summary composed from the event fields. */
const eventMeta = (event: MarqueeEventDetail) =>
	[
		formatDate(event.date, 'PP'),
		formatStartTime(event.start_time, event.time_zone, event.date),
		eventPlaceName(event),
		event.hashtag
	]
		.filter(Boolean)
		.join(' · ')

/** Context exposed to the tab pages so each can set its own title-bar action. */
interface MarqueeEventDetailContext {
	setEndSlot: (node: ReactNode) => void
	/**
	 * The sticky tab bar. Its rendered bottom edge is where scrolled content
	 * should come to rest (below the app header + tabs), so pages can align an
	 * element to the top of the usable area without hardcoding that offset.
	 */
	stickyHeaderRef: RefObject<HTMLDivElement | null>
}

export function useMarqueeEventDetailContext() {
	return useOutletContext<MarqueeEventDetailContext>()
}

function MarqueeEventDetailLayout() {
	const { t } = useTranslation()
	const { id } = useParams<{ id: string }>()
	const { data: event, isLoading } = useQuery(eventQueryOptions(id))
	const { data: ctas = [] } = useQuery(ctasQueryOptions())
	// The resolution loop is creator-only, so the wrapper it shares with the
	// tab's action is gated here too: otherwise a facilitator on a tab with no
	// action of its own gets an empty flex item and its gap under the title.
	const { data: operator } = useQuery(operatorQueryOptions())
	const showResolution =
		event !== undefined && isMarketManagerAllowed(operator, 'resolveEvent')

	const ctaLabel = useMemo(() => {
		const byCode = new Map(ctas.map((cta) => [cta.code, cta.label]))
		return (code: string) => byCode.get(code) ?? code
	}, [ctas])
	const pitchCtas = event?.ctas ?? []
	const [endSlot, setEndSlot] = useState<ReactNode>(null)

	// Keep the active tab centered in the horizontal scroller (mirrors the
	// events-list tabs) — re-runs whenever the route switches tab.
	const { pathname } = useLocation()
	const tabsViewportRef = useRef<HTMLDivElement>(null)
	const stickyHeaderRef = useRef<HTMLDivElement>(null)
	useEffect(() => {
		const container = tabsViewportRef.current
		const activeTab = container?.querySelector<HTMLElement>(
			`[href="${pathname}"]`
		)
		if (!(container && activeTab)) {
			return
		}
		const tabCenter = activeTab.offsetLeft + activeTab.offsetWidth / 2
		container.scrollLeft = Math.max(
			0,
			tabCenter - container.clientWidth / 2
		)
	}, [pathname])

	const tabs = [
		{
			label: t('marquee:tab_attendees'),
			to: generatePath(routes.MARQUEE_EVENT_ATTENDEES.handle.fullPath, {
				id: id ?? ''
			})
		},
		{
			label: t('marquee:tab_check_in'),
			to: generatePath(routes.MARQUEE_EVENT_CHECK_IN.handle.fullPath, {
				id: id ?? ''
			})
		},
		{
			label: t('marquee:tab_export'),
			to: generatePath(routes.MARQUEE_EVENT_EXPORT.handle.fullPath, {
				id: id ?? ''
			})
		},
		{
			label: t('marquee:tab_report'),
			to: generatePath(routes.MARQUEE_EVENT_REPORT.handle.fullPath, {
				id: id ?? ''
			}),
			// The report only exists once the event has closed. Disabled while
			// the state says otherwise, including before the detail has loaded.
			disabled: event?.state !== 'closed'
		}
	]

	return (
		<div className='flex flex-1 flex-col'>
			{/* Breadcrumb */}
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<MarqueeBreadcrumbs currentLabel={event?.name} />
			</div>

			{/* Title section */}
			<div className='flex l:flex-row flex-col l:items-start l:justify-between gap-4 l:px-10 px-4 pt-4 pb-6'>
				<div className='flex min-w-0 flex-col gap-2'>
					{isLoading || !event ? (
						<>
							{/* Title + status badge row — wraps and caps widths so
							    it never overflows a narrow phone. */}
							<div className='flex max-w-full flex-wrap items-center gap-3'>
								<Skeleton height={24} width={240} />
								<Skeleton height={22} width={96} />
							</div>
							{/* Meta line */}
							<Skeleton height={16} width={260} />
						</>
					) : (
						<>
							<div className='flex flex-wrap items-center gap-3'>
								<h1 className='font-medium text-neutral-900 text-xl'>
									{event.name}
								</h1>
								<EventStatusBadge event={event} />
							</div>
							<p className='text-neutral-500 text-sm'>
								{eventMeta(event)}
							</p>
							{pitchCtas.length > 0 && (
								<div className='flex flex-wrap items-center gap-2'>
									<span className='text-neutral-500 text-sm'>
										{t('marquee:ctas_section_title')}
									</span>
									{pitchCtas.map((code) => (
										<span
											className='rounded-full bg-grey-100 px-3 py-1 font-medium text-neutral-700 text-xs'
											key={code}
										>
											{ctaLabel(code)}
										</span>
									))}
								</div>
							)}
							{event.assigned_mms &&
								event.assigned_mms.length > 0 && (
									<div className='flex flex-wrap items-center gap-2'>
										<span className='text-neutral-500 text-sm'>
											{t(
												'marquee:managers_section_title'
											)}
										</span>
										{event.assigned_mms.map((manager) => (
											<span
												className='rounded-full bg-grey-100 px-3 py-1 font-medium text-neutral-700 text-xs'
												key={manager.id}
											>
												{manager.name ??
													manager.email ??
													manager.id}
											</span>
										))}
									</div>
								)}
						</>
					)}
				</div>

				{/* The tab's own action first, then the resolution loop, which
				    belongs to the event rather than to a tab: Download worklist
				    and Refresh now read and rewrite the roster every tab shows,
				    so they sit here for all of them. Stacked on mobile, side by
				    side from `l` up. */}
				{(endSlot || showResolution) && (
					<div className='flex l:w-auto w-full shrink-0 l:flex-row flex-col l:items-center items-stretch gap-2 self-start'>
						{endSlot}
						{showResolution && <ResolutionActions event={event} />}
					</div>
				)}
			</div>

			{/* Tabs — sticky under the sticky app header (64px). The wrapper keeps
			    the full-width bottom divider + sticky background; the
			    HorizontalScroller inside handles horizontal overflow when the
			    labels don't fit (same pattern as the events list tabs). */}
			<div
				className='sticky top-16 z-(--z-sticky-header) border-neutral-300 border-b bg-neutral-white'
				ref={stickyHeaderRef}
			>
				<HorizontalScroller viewportRef={tabsViewportRef}>
					<div className='flex w-max items-center gap-6 l:px-10 px-4 pt-2'>
						{tabs.map((tab) => (
							<TabLink
								disabled={tab.disabled}
								key={tab.to}
								label={tab.label}
								to={tab.to}
							/>
						))}
					</div>
				</HorizontalScroller>
			</div>

			{/* Tab content flows with the body scroll. */}
			<div className='flex flex-1 flex-col'>
				<Outlet
					context={
						{
							setEndSlot,
							stickyHeaderRef
						} satisfies MarqueeEventDetailContext
					}
				/>
			</div>
		</div>
	)
}

export { MarqueeEventDetailLayout }
